import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MapPin, Phone, Clock } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { submitMessage } from "../lib/api";

const Contact = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await submitMessage(form);
      setStatus({ state: "success", msg: t("contact.form.success") });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ state: "error", msg: t("contact.form.error") });
    }
  };

  return (
    <section
      id="contact"
      className="bg-mayak-bg-alt text-mayak-text relative py-24 lg:py-36 mayak-grain overflow-hidden"
      data-testid="contact-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <span className="mayak-eyebrow">{t("contact.eyebrow")}</span>
            <h2 className="mt-6 font-black text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.02] text-mayak-text">
              {t("contact.title")}
            </h2>
            <p className="mt-6 max-w-xl text-mayak-dim leading-relaxed">
              {t("contact.subtitle")}
            </p>

            <form
              onSubmit={onSubmit}
              className="mt-12 grid sm:grid-cols-2 gap-x-8 gap-y-2"
              data-testid="contact-form"
            >
              <div className="sm:col-span-1">
                <label className="text-[10px] uppercase tracking-[0.3em] text-mayak-faint mb-1 block">
                  {t("contact.form.name")}
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="mayak-input"
                  data-testid="contact-input-name"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="text-[10px] uppercase tracking-[0.3em] text-mayak-faint mb-1 block">
                  {t("contact.form.email")}
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mayak-input"
                  data-testid="contact-input-email"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="text-[10px] uppercase tracking-[0.3em] text-mayak-faint mb-1 block mt-6">
                  {t("contact.form.phone")}
                </label>
                <input
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mayak-input"
                  data-testid="contact-input-phone"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="text-[10px] uppercase tracking-[0.3em] text-mayak-faint mb-1 block mt-6">
                  {t("contact.form.subject")}
                </label>
                <input
                  value={form.subject}
                  onChange={(e) => update("subject", e.target.value)}
                  className="mayak-input"
                  data-testid="contact-input-subject"
                />
              </div>
              <div className="sm:col-span-2 mt-6">
                <label className="text-[10px] uppercase tracking-[0.3em] text-mayak-faint mb-1 block">
                  {t("contact.form.message")}
                </label>
                <textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="mayak-input resize-none"
                  data-testid="contact-input-message"
                />
              </div>

              <div className="sm:col-span-2 mt-10 flex flex-wrap items-center gap-6">
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  className="magnetic-btn"
                  data-testid="contact-submit-button"
                >
                  {status.state === "loading"
                    ? t("contact.form.sending")
                    : t("contact.form.send")}
                  <ArrowRight size={16} />
                </button>
                {status.msg && (
                  <motion.span
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`text-sm ${
                      status.state === "success" ? "text-mayak-accent" : "text-red-500"
                    }`}
                    data-testid="contact-form-status"
                  >
                    {status.msg}
                  </motion.span>
                )}
              </div>
            </form>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] border border-mayak-border overflow-hidden bg-mayak-surface">
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, hsl(var(--mayak-accent) / 0.4) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--mayak-accent) / 0.4) 1px, transparent 1px)",
                  backgroundSize: "40px 40px"
                }}
              />
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 400 300"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M0,200 C80,150 120,170 200,180 C280,190 350,140 400,160"
                  stroke="hsl(var(--mayak-accent))"
                  strokeWidth="1.4"
                  strokeOpacity="0.55"
                />
                <path
                  d="M0,120 C100,100 200,140 300,90 C340,70 380,80 400,100"
                  stroke="hsl(var(--mayak-accent))"
                  strokeWidth="1"
                  strokeOpacity="0.35"
                />
                <path
                  d="M120,0 L160,300"
                  stroke="hsl(var(--mayak-accent))"
                  strokeWidth="0.8"
                  strokeOpacity="0.25"
                />
                <path
                  d="M280,0 L260,300"
                  stroke="hsl(var(--mayak-accent))"
                  strokeWidth="0.8"
                  strokeOpacity="0.25"
                />
              </svg>
              <motion.div
                animate={{ scale: [1, 1.4, 1], opacity: [0.7, 0, 0.7] }}
                transition={{ duration: 2.4, repeat: Infinity }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border border-mayak-accent"
              />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-mayak-accent-bright mayak-glow" />
              <div className="absolute bottom-4 left-4 text-[10px] tracking-[0.3em] uppercase text-mayak-accent font-mono">
                41.0843° N · 28.9784° E
              </div>
            </div>

            <div className="mt-10 space-y-7">
              <InfoBlock
                icon={MapPin}
                label={t("contact.info.office")}
                value={t("contact.info.address")}
              />
              <InfoBlock
                icon={Mail}
                label={t("contact.info.emailLabel")}
                value={t("contact.info.email")}
              />
              <InfoBlock
                icon={Phone}
                label={t("contact.info.phoneLabel")}
                value={t("contact.info.phone")}
              />
              <InfoBlock
                icon={Clock}
                label={t("contact.info.hoursLabel")}
                value={t("contact.info.hours")}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const InfoBlock = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <Icon className="text-mayak-accent shrink-0 mt-1" size={18} strokeWidth={1.5} />
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-mayak-faint mb-1">
        {label}
      </div>
      <div className="text-mayak-text text-base">{value}</div>
    </div>
  </div>
);

export default Contact;
